import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/errors/use-toast';
import { MentionTriggeredAIService } from './services/mention-triggered-ai.service';
import { hasMentions } from '@/utils/mention-utils';
import { TablesInsert, Tables } from '@/integrations/supabase/types';
import { useCommunityAIWorkflow, useHasCategoryAgents } from './use-community-ai';
import { logInfo, logError, LogCategory } from '@/utils/logger';

export type CommunityDiscussion = Tables<'community_discussions'>;

export type CommunityCategory = Tables<'community_discussion_categories'>;

export interface CreateDiscussionInput {
  title: string;
  content: string;
  category: string;
}

// Fetch discussions with user information
export const useCommunityDiscussions = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['community-discussions', user?.id],
    queryFn: async () => {
      // First get discussions
      const { data: discussions, error: discussionsError } = await supabase
        .from('community_discussions')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (discussionsError) throw discussionsError;
      
      // Then get user information for each discussion
      const discussionsWithUsers = await Promise.all(
        discussions.map(async (discussion) => {
          const { data: postAuthor } = await supabase
            .from('public_profile')
            .select('id, first_name, profile_picture_url')
            .eq('id', discussion.user_id)
            .single();
          
          return {
            ...discussion,
            users: postAuthor
          };
        })
      );
      
      return discussionsWithUsers;
    },
  });
};

// Create discussion with AI integration
export const useCreateDiscussion = (options?: { navigateToPost?: boolean }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { executeWorkflow: executeAIWorkflow } = useCommunityAIWorkflow();

  return useMutation({
    mutationFn: async (discussionData: CreateDiscussionInput) => {
      if (!user?.id) throw new Error('User not authenticated');

      logInfo('Creating community discussion', { 
        category: LogCategory.USER_ACTION,
        data: { 
          title: discussionData.title,
          category: discussionData.category,
          contentLength: discussionData.content.length,
          userId: user.id
        }
      });

      // Step 1: Create the discussion post
      const insertData: TablesInsert<'community_discussions'> = {
        title: discussionData.title,
        content: discussionData.content,
        category: discussionData.category,
        user_id: user.id,
        likes: 0,
        replies: 0,
        is_hot: false,
      };

      const { data: discussion, error } = await supabase
        .from('community_discussions')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      logInfo('Community discussion created successfully', { 
        category: LogCategory.USER_ACTION,
        data: { 
          discussionId: discussion.id,
          category: discussionData.category
        }
      });

      // Step 2: Trigger AI responses asynchronously (fire-and-forget - don't block post creation)
      executeAIWorkflow(
        discussionData.content,
        discussion.id,
        discussionData.category,
        user.id
      ).then(async (aiResult) => {
        // Update discussion with OpenAI thread ID after AI workflow completes
        if (aiResult.openaiThreadId) {
          const { error: updateError } = await supabase
            .from('community_discussions')
            .update({ openai_thread_id: aiResult.openaiThreadId })
            .eq('id', discussion.id);

          if (updateError) {
            logError('Failed to update discussion with OpenAI thread ID', {
              category: LogCategory.DATABASE,
              data: { 
                error: updateError.message, 
                discussionId: discussion.id,
                openaiThreadId: aiResult.openaiThreadId
              }
            });
          } else {
            logInfo('Discussion updated with OpenAI thread ID', { 
              category: LogCategory.DATABASE,
              data: { 
                discussionId: discussion.id,
                openaiThreadId: aiResult.openaiThreadId
              }
            });
          }
        }

        logInfo('Community AI workflow completed', { 
          category: LogCategory.USER_ACTION,
          data: { 
            discussionId: discussion.id,
            aiSuccess: aiResult.aiResult.success,
            responseCount: aiResult.aiResult.responses.length
          }
        });
      }).catch((aiError: any) => {
        // Don't fail post creation if AI fails - just log the error
        logError('Community AI workflow failed (non-critical)', {
          category: LogCategory.USER_ACTION,
          data: { 
            error: aiError.message,
            discussionId: discussion.id,
            category: discussionData.category
          }
        });
        
        // Note: We don't show a toast here since the post creation already succeeded
        // The user will see AI responses appear later when they're ready
      });

      return discussion as CommunityDiscussion;
    },
    onMutate: async (variables: CreateDiscussionInput) => {
      // Cancel any outgoing refetches for both old and new query patterns
      await queryClient.cancelQueries({ queryKey: ['community-discussions', user?.id] });
      await queryClient.cancelQueries({ queryKey: ['community-discussions-paginated'] });
      
      // Snapshot the previous values
      const previousCommunityData = queryClient.getQueryData(['community-discussions', user?.id]);
      const previousPaginatedData = queryClient.getQueriesData({ queryKey: ['community-discussions-paginated'] });
      
      // Get current user data for optimistic post
      const { data: userData } = await supabase
        .from('public_profile')
        .select('id, first_name, profile_picture_url')
        .eq('id', user!.id)
        .single();
      
      // Create optimistic discussion
      const optimisticDiscussion = {
        id: `temp-${Date.now()}`, // Temporary ID
        title: variables.title,
        content: variables.content,
        category: variables.category,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        likes: 0,
        replies: 0,
        views: 0,
        is_hot: false,
        user_id: user!.id,
        openai_thread_id: null,
        users: {
          id: user!.id,
          first_name: userData?.first_name || 'You',
          profile_picture_url: userData?.profile_picture_url || null
        }
      };
      
      // Optimistically add to paginated queries (community page)
      queryClient.setQueriesData(
        { queryKey: ['community-discussions-paginated'] },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;
          
          // Create optimistic paginated discussion format
          const optimisticPaginatedDiscussion = {
            discussion_id: optimisticDiscussion.id,
            discussion_title: optimisticDiscussion.title,
            discussion_content: optimisticDiscussion.content,
            discussion_category: optimisticDiscussion.category,
            discussion_created_at: optimisticDiscussion.created_at,
            discussion_updated_at: optimisticDiscussion.updated_at,
            discussion_likes: optimisticDiscussion.likes,
            discussion_replies: optimisticDiscussion.replies,
            discussion_views: optimisticDiscussion.views,
            discussion_is_hot: optimisticDiscussion.is_hot,
            discussion_user_id: optimisticDiscussion.user_id,
            discussion_openai_thread_id: optimisticDiscussion.openai_thread_id,
            discussion_author_id: optimisticDiscussion.users.id,
            discussion_author_first_name: optimisticDiscussion.users.first_name,
            discussion_author_profile_picture_url: optimisticDiscussion.users.profile_picture_url,
            discussion_is_liked_by_user: false,
            total_count: oldData.pages[0]?.pagination?.totalCount || 0,
            has_more_pages: oldData.pages[0]?.pagination?.hasMorePages || false,
            current_page: oldData.pages[0]?.pagination?.currentPage || 0,
            total_pages: oldData.pages[0]?.pagination?.totalPages || 0,
            category_counts: oldData.pages[0]?.categoryCounts || {}
          };
          
          return {
            ...oldData,
            pages: oldData.pages.map((page: any, index: number) => {
              if (index === 0) {
                // Add to first page
                return {
                  ...page,
                  discussions: [optimisticPaginatedDiscussion, ...page.discussions],
                  pagination: {
                    ...page.pagination,
                    totalCount: page.pagination.totalCount + 1
                  }
                };
              }
              return page;
            })
          };
        }
      );
      
      // Optimistically add to legacy community discussions query
      queryClient.setQueryData(['community-discussions', user?.id], (oldData: any) => {
        if (!oldData) return [optimisticDiscussion];
        return [optimisticDiscussion, ...oldData];
      });
      
      return { 
        previousCommunityData, 
        previousPaginatedData 
      };
    },
    onError: (err, variables, context) => {
      // Rollback optimistic updates on error
      if (context?.previousCommunityData) {
        queryClient.setQueryData(['community-discussions', user?.id], context.previousCommunityData);
      }
      
      if (context?.previousPaginatedData) {
        context.previousPaginatedData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      
      logError('Failed to create community discussion', {
        category: LogCategory.USER_ACTION,
        data: { 
          error: err.message,
          title: variables.title,
          category: variables.category
        }
      });

      toast({
        title: "Error creating post",
        description: err.message || "Failed to create post",
        variant: "destructive",
      });
    },
    onSuccess: (discussion, variables) => {
      // Invalidate relevant queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['community-discussions', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['community-discussions-paginated'] });
      
      logInfo('Community discussion creation completed', { 
        category: LogCategory.USER_ACTION,
        data: { 
          discussionId: discussion.id,
          category: variables.category
        }
      });

      // Navigate to the new post if requested
      if (options?.navigateToPost) {
        navigate(`/community/post/${discussion.id}`);
      }

      toast({
        title: "Post created",
        description: options?.navigateToPost 
          ? "Your post has been published! You can now watch for AI responses."
          : "Your post has been published to the community",
        variant: "success",
      });
    },
  });
};

// Fetch community categories
export const useCommunityCategories = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['community-categories', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('community_discussion_categories')
        .select('*')
        .order('priority', { ascending: true, nullsFirst: false })
        .order('name', { ascending: true });
      
      if (error) throw error;
      return data as CommunityCategory[];
    },
  });
};

// Get category count for each category
export const useCategoryCounts = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['community-category-counts', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('community_discussions')
        .select('category')
        .order('category');
      
      if (error) throw error;
      
      // Count discussions by category
      const counts: Record<string, number> = {};
      data.forEach(discussion => {
        counts[discussion.category] = (counts[discussion.category] || 0) + 1;
      });
      
      return counts;
    },
  });
};

// Get a single discussion by ID with user info
export const useDiscussionById = (discussionId: string) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['community-discussion', discussionId, user?.id],
    queryFn: async () => {
      // Get the discussion
      const { data: discussion, error: discussionError } = await supabase
        .from('community_discussions')
        .select('*')
        .eq('id', discussionId)
        .single();
      
      if (discussionError) throw discussionError;
      
      // Get user information
      const { data: postAuthor } = await supabase
        .from('public_profile')
        .select('id, first_name, profile_picture_url')
        .eq('id', discussion.user_id)
        .single();
      
      return {
        ...discussion,
        users: postAuthor
      };
    },
    enabled: !!discussionId,
  });
};

// Get replies for a discussion
export const useDiscussionReplies = (discussionId: string) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['community-discussion-replies', discussionId, user?.id],
    queryFn: async () => {
      // Get replies with left join to agent_assistants for AI responses
      const { data: replies, error: repliesError } = await supabase
        .from('community_discussion_replies')
        .select(`
          *,
          agent_assistants (
            id,
            name,
            openai_assistant_id,
            profile_picture_url
          )
        `)
        .eq('discussion_id', discussionId)
        .order('created_at', { ascending: true });
      
      if (repliesError) throw repliesError;
      
      // Get user information for each reply (only for non-AI responses)
      const repliesWithUsers = await Promise.all(
        replies.map(async (reply) => {
          let replyAuthor = null;
          
          // Only fetch user data for non-AI responses
          if (!reply.is_ai_response && reply.user_id) {
            const { data: userData } = await supabase
              .from('public_profile')
              .select('id, first_name, profile_picture_url')
              .eq('id', reply.user_id)
              .single();
            replyAuthor = userData;
          }
          
          return {
            ...reply,
            users: replyAuthor
          };
        })
      );
      
      return repliesWithUsers;
    },
    enabled: !!discussionId,
  });
};

// Create a new reply
export const useCreateReply = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (data: { discussionId: string; content: string }) => {
      if (!user) throw new Error('User not authenticated');
      
      const { data: reply, error } = await supabase
        .from('community_discussion_replies')
        .insert({
          discussion_id: data.discussionId,
          content: data.content,
          user_id: user.id,
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Update reply count in discussion
      const { error: updateError } = await supabase
        .from('community_discussions')
        .update({
          replies: (await supabase
            .from('community_discussion_replies')
            .select('id', { count: 'exact' })
            .eq('discussion_id', data.discussionId)
          ).count || 0
        })
        .eq('id', data.discussionId);
      
      if (updateError) console.warn('Failed to update reply count:', updateError);
      
      // Process mentions asynchronously (fire-and-forget - don't block reply creation)
      if (hasMentions(data.content)) {
        logInfo('Reply contains mentions, triggering AI responses', {
          category: LogCategory.USER_ACTION,
          data: {
            discussionId: data.discussionId,
            replyId: reply.id,
            contentLength: data.content.length
          }
        });

        // Trigger mention-based AI responses asynchronously
        MentionTriggeredAIService.processMentionsInReply(
          data.content,
          data.discussionId,
          user.id
        ).then((mentionResult) => {
          logInfo('Mention-triggered AI responses completed', {
            category: LogCategory.USER_ACTION,
            data: {
              discussionId: data.discussionId,
              replyId: reply.id,
              mentionedAgents: mentionResult.mentionedAgents,
              successCount: mentionResult.responses.filter(r => r.success).length,
              errorCount: mentionResult.responses.filter(r => !r.success).length
            }
          });

          // Invalidate queries to show new AI responses
          queryClient.invalidateQueries({ 
            queryKey: ['discussion-thread-data', data.discussionId, user?.id] 
          });

          // AI responses are generated silently - no toast notifications needed
          // Users will see the responses appear in the discussion thread
        }).catch((mentionError) => {
          logError('Mention-triggered AI responses failed (non-critical)', {
            category: LogCategory.USER_ACTION,
            data: {
              error: mentionError.message,
              discussionId: data.discussionId,
              replyId: reply.id
            }
          });
          
          // Don't show error toast since the reply was posted successfully
          // The mention processing failure is non-critical
        });
      }
      
      return reply;
    },
    onMutate: async (variables: { discussionId: string; content: string }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['discussion-thread-data', variables.discussionId, user?.id] });
      
      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['discussion-thread-data', variables.discussionId, user?.id]);
      
      // Get current user data for optimistic reply
      const { data: userData } = await supabase
        .from('public_profile')
        .select('id, first_name, profile_picture_url')
        .eq('id', user!.id)
        .single();
      
      // Create optimistic reply
      const optimisticReply = {
        id: `temp-${Date.now()}`, // Temporary ID
        content: variables.content,
        created_at: new Date().toISOString(),
        likes: 0,
        user_id: user!.id,
        is_ai_response: false,
        assistant_id: null,
        is_liked_by_user: false,
        author: {
          id: user!.id,
          first_name: userData?.first_name || 'You',
          profile_picture_url: userData?.profile_picture_url || null
        },
        ai_assistant: null
      };
      
      // Optimistically update the cache
      queryClient.setQueryData(['discussion-thread-data', variables.discussionId, user?.id], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          discussion: {
            ...old.discussion,
            replies: (old.discussion.replies || 0) + 1
          },
          replies: [...old.replies, optimisticReply]
        };
      });
      
      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(['discussion-thread-data', variables.discussionId, user?.id], context.previousData);
      }
      console.error('Error creating reply:', err);
    },
    onSuccess: (_, variables) => {
      // Refetch to ensure data consistency and get the real reply ID
      queryClient.invalidateQueries({ 
        queryKey: ['discussion-thread-data', variables.discussionId, user?.id] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['community-discussions', user?.id] 
      });
    },
  });
};

// Toggle like on a discussion
export const useToggleDiscussionLike = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (discussionId: string) => {
      if (!user) throw new Error('User not authenticated');
      
      // Check if already liked - use maybeSingle() to avoid 406 errors
      const { data: existingLike, error: likeCheckError } = await supabase
        .from('community_discussion_likes')
        .select('id')
        .eq('discussion_id', discussionId)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (likeCheckError) throw likeCheckError;
      
      if (existingLike) {
        // Unlike
        const { error } = await supabase
          .from('community_discussion_likes')
          .delete()
          .eq('discussion_id', discussionId)
          .eq('user_id', user.id);
        
        if (error) throw error;
        
        return { action: 'unliked' };
      } else {
        // Like
        const { error } = await supabase
          .from('community_discussion_likes')
          .insert({
            discussion_id: discussionId,
            user_id: user.id,
          });
        
        if (error) throw error;
        
        return { action: 'liked' };
      }
    },
    onMutate: async (discussionId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['discussion-thread-data', discussionId, user?.id] });
      await queryClient.cancelQueries({ queryKey: ['community-discussions-paginated'] });
      
      // Snapshot the previous values
      const previousData = queryClient.getQueryData(['discussion-thread-data', discussionId, user?.id]);
      const previousPaginatedData = queryClient.getQueriesData({ queryKey: ['community-discussions-paginated'] });
      
      // Optimistically update the discussion thread data
      queryClient.setQueryData(['discussion-thread-data', discussionId, user?.id], (old: any) => {
        if (!old) return old;
        
        const currentlyLiked = old.discussion.is_liked_by_user;
        const currentLikes = old.discussion.likes || 0;
        
        return {
          ...old,
          discussion: {
            ...old.discussion,
            is_liked_by_user: !currentlyLiked,
            likes: currentlyLiked ? currentLikes - 1 : currentLikes + 1
          }
        };
      });
      
      // Optimistically update the paginated discussions data
      queryClient.setQueriesData(
        { queryKey: ['community-discussions-paginated'] },
        (old: any) => {
          if (!old?.pages) return old;
          
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              discussions: page.discussions.map((discussion: any) => {
                if (discussion.discussion_id === discussionId) {
                  const currentlyLiked = discussion.discussion_is_liked_by_user;
                  const currentLikes = discussion.discussion_likes || 0;
                  
                  return {
                    ...discussion,
                    discussion_is_liked_by_user: !currentlyLiked,
                    discussion_likes: currentlyLiked ? currentLikes - 1 : currentLikes + 1
                  };
                }
                return discussion;
              })
            }))
          };
        }
      );
      
      return { previousData, previousPaginatedData };
    },
    onError: (err, discussionId, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(['discussion-thread-data', discussionId, user?.id], context.previousData);
      }
      if (context?.previousPaginatedData) {
        context.previousPaginatedData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error('Error toggling like:', err);
    },
    onSettled: (result, error, discussionId) => {
      // Always refetch after success or error
      queryClient.invalidateQueries({ queryKey: ['discussion-thread-data', discussionId, user?.id] });
      queryClient.invalidateQueries({ queryKey: ['community-discussions', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['community-discussions-paginated'] });
    },
  });
};

// Toggle like on a reply
export const useToggleReplyLike = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (data: { replyId: string; discussionId: string }) => {
      if (!user) throw new Error('User not authenticated');
      
      // Check if already liked - use maybeSingle() to avoid 406 errors
      const { data: existingLike, error: likeCheckError } = await supabase
        .from('community_discussion_likes')
        .select('id')
        .eq('reply_id', data.replyId)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (likeCheckError) throw likeCheckError;
      
      if (existingLike) {
        // Unlike
        const { error } = await supabase
          .from('community_discussion_likes')
          .delete()
          .eq('reply_id', data.replyId)
          .eq('user_id', user.id);
        
        if (error) throw error;
        
        return { action: 'unliked' };
      } else {
        // Like
        const { error } = await supabase
          .from('community_discussion_likes')
          .insert({
            reply_id: data.replyId,
            user_id: user.id,
          });
        
        if (error) throw error;
        
        return { action: 'liked' };
      }
    },
    onMutate: async (variables: { replyId: string; discussionId: string }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['discussion-thread-data', variables.discussionId, user?.id] });
      
      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['discussion-thread-data', variables.discussionId, user?.id]);
      
      // Optimistically update the cache
      queryClient.setQueryData(['discussion-thread-data', variables.discussionId, user?.id], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          replies: old.replies.map((reply: any) => {
            if (reply.id === variables.replyId) {
              const currentlyLiked = reply.is_liked_by_user;
              const currentLikes = reply.likes || 0;
              
              return {
                ...reply,
                is_liked_by_user: !currentlyLiked,
                likes: currentlyLiked ? currentLikes - 1 : currentLikes + 1
              };
            }
            return reply;
          })
        };
      });
      
      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(['discussion-thread-data', variables.discussionId, user?.id], context.previousData);
      }
      console.error('Error toggling reply like:', err);
    },
    onSettled: (result, error, variables) => {
      // Always refetch after success or error
      queryClient.invalidateQueries({ queryKey: ['discussion-thread-data', variables.discussionId, user?.id] });
    },
  });
};

// Update a reply
export const useUpdateReply = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (data: { replyId: string; content: string; discussionId: string }) => {
      if (!user) throw new Error('User not authenticated');
      
      // First check if the user owns this reply
      const { data: reply, error: checkError } = await supabase
        .from('community_discussion_replies')
        .select('user_id, is_ai_response')
        .eq('id', data.replyId)
        .single();
      
      if (checkError) throw checkError;
      if (!reply) throw new Error('Reply not found');
      
      // Don't allow editing AI responses
      if (reply.is_ai_response) {
        throw new Error('Cannot edit AI responses');
      }
      
      // Check ownership
      if (reply.user_id !== user.id) {
        throw new Error('You can only edit your own replies');
      }
      
      // Update the reply
      const { data: updatedReply, error } = await supabase
        .from('community_discussion_replies')
        .update({
          content: data.content,
          updated_at: new Date().toISOString()
        })
        .eq('id', data.replyId)
        .select()
        .single();
      
      if (error) throw error;
      
      return updatedReply;
    },
    onMutate: async (variables: { replyId: string; content: string; discussionId: string }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['discussion-thread-data', variables.discussionId, user?.id] });
      
      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['discussion-thread-data', variables.discussionId, user?.id]);
      
      // Optimistically update the cache
      queryClient.setQueryData(['discussion-thread-data', variables.discussionId, user?.id], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          replies: old.replies.map((reply: any) => {
            if (reply.id === variables.replyId) {
              return {
                ...reply,
                content: variables.content,
                updated_at: new Date().toISOString()
              };
            }
            return reply;
          })
        };
      });
      
      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(['discussion-thread-data', variables.discussionId, user?.id], context.previousData);
      }
      console.error('Error updating reply:', err);
    },
    onSuccess: (_, variables) => {
      // Refetch to ensure data consistency
      queryClient.invalidateQueries({ 
        queryKey: ['discussion-thread-data', variables.discussionId, user?.id] 
      });
    },
  });
};

// Delete a reply
export const useDeleteReply = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (data: { replyId: string; discussionId: string }) => {
      if (!user) throw new Error('User not authenticated');
      
      // First check if the user owns this reply
      const { data: reply, error: checkError } = await supabase
        .from('community_discussion_replies')
        .select('user_id, is_ai_response')
        .eq('id', data.replyId)
        .single();
      
      if (checkError) throw checkError;
      if (!reply) throw new Error('Reply not found');
      
      // Don't allow deleting AI responses
      if (reply.is_ai_response) {
        throw new Error('Cannot delete AI responses');
      }
      
      // Check ownership
      if (reply.user_id !== user.id) {
        throw new Error('You can only delete your own replies');
      }
      
      // Delete the reply
      const { error } = await supabase
        .from('community_discussion_replies')
        .delete()
        .eq('id', data.replyId);
      
      if (error) throw error;
      
      // Update reply count in discussion
      const { count } = await supabase
        .from('community_discussion_replies')
        .select('id', { count: 'exact' })
        .eq('discussion_id', data.discussionId);

      const { error: updateError } = await supabase
        .from('community_discussions')
        .update({ replies: count || 0 })
        .eq('id', data.discussionId);
      
      if (updateError) console.warn('Failed to update reply count:', updateError);
      
      return { deletedReplyId: data.replyId };
    },
    onMutate: async (variables: { replyId: string; discussionId: string }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['discussion-thread-data', variables.discussionId, user?.id] });
      
      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['discussion-thread-data', variables.discussionId, user?.id]);
      
      // Optimistically update the cache
      queryClient.setQueryData(['discussion-thread-data', variables.discussionId, user?.id], (old: any) => {
        if (!old) return old;
        
        // Remove the reply from the list and update reply count
        const filteredReplies = old.replies.filter((reply: any) => reply.id !== variables.replyId);
        
        return {
          ...old,
          discussion: {
            ...old.discussion,
            replies: filteredReplies.length
          },
          replies: filteredReplies
        };
      });
      
      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(['discussion-thread-data', variables.discussionId, user?.id], context.previousData);
      }
      console.error('Error deleting reply:', err);
    },
    onSuccess: (_, variables) => {
      // Refetch to ensure data consistency
      queryClient.invalidateQueries({ 
        queryKey: ['discussion-thread-data', variables.discussionId, user?.id] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['community-discussions', user?.id] 
      });
    },
  });
};

// Update a discussion
export const useUpdateDiscussion = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (data: { discussionId: string; title: string; content: string }) => {
      if (!user) throw new Error('User not authenticated');
      
      // First check if the user owns this discussion
      const { data: discussion, error: checkError } = await supabase
        .from('community_discussions')
        .select('user_id')
        .eq('id', data.discussionId)
        .single();
      
      if (checkError) throw checkError;
      if (!discussion) throw new Error('Discussion not found');
      
      // Check ownership
      if (discussion.user_id !== user.id) {
        throw new Error('You can only edit your own posts');
      }
      
      // Update the discussion
      const { data: updatedDiscussion, error } = await supabase
        .from('community_discussions')
        .update({
          title: data.title,
          content: data.content,
          updated_at: new Date().toISOString()
        })
        .eq('id', data.discussionId)
        .select()
        .single();
      
      if (error) throw error;
      
      return updatedDiscussion;
    },
    onMutate: async (variables: { discussionId: string; title: string; content: string }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['discussion-thread-data', variables.discussionId, user?.id] });
      
      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['discussion-thread-data', variables.discussionId, user?.id]);
      
      // Optimistically update the cache
      queryClient.setQueryData(['discussion-thread-data', variables.discussionId, user?.id], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          discussion: {
            ...old.discussion,
            title: variables.title,
            content: variables.content,
            updated_at: new Date().toISOString()
          }
        };
      });
      
      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(['discussion-thread-data', variables.discussionId, user?.id], context.previousData);
      }
      console.error('Error updating discussion:', err);
    },
    onSuccess: (_, variables) => {
      // Refetch to ensure data consistency
      queryClient.invalidateQueries({ 
        queryKey: ['discussion-thread-data', variables.discussionId, user?.id] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['community-discussions', user?.id] 
      });
    },
  });
};

// Delete a discussion
export const useDeleteDiscussion = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (discussionId: string) => {
      if (!user) throw new Error('User not authenticated');
      
      // First check if the user owns this discussion
      const { data: discussion, error: checkError } = await supabase
        .from('community_discussions')
        .select('user_id')
        .eq('id', discussionId)
        .single();
      
      if (checkError) throw checkError;
      if (!discussion) throw new Error('Discussion not found');
      
      // Check ownership
      if (discussion.user_id !== user.id) {
        throw new Error('You can only delete your own posts');
      }
      
      // Delete all related data first
      // Delete likes on the discussion and its replies
      const { error: likesError } = await supabase
        .from('community_discussion_likes')
        .delete()
        .eq('discussion_id', discussionId);
      
      if (likesError) console.warn('Failed to delete discussion likes:', likesError);
      
      // Delete all replies to the discussion
      const { error: repliesError } = await supabase
        .from('community_discussion_replies')
        .delete()
        .eq('discussion_id', discussionId);
      
      if (repliesError) console.warn('Failed to delete discussion replies:', repliesError);
      
      // Finally delete the discussion itself
      const { error } = await supabase
        .from('community_discussions')
        .delete()
        .eq('id', discussionId);
      
      if (error) throw error;
      
      return { deletedDiscussionId: discussionId };
    },
    onMutate: async (discussionId: string) => {
      // Cancel any outgoing refetches for both old and new query patterns
      await queryClient.cancelQueries({ queryKey: ['community-discussions', user?.id] });
      await queryClient.cancelQueries({ queryKey: ['community-discussions-paginated'] });
      await queryClient.cancelQueries({ queryKey: ['discussion-thread-data', discussionId, user?.id] });
      
      // Snapshot the previous values
      const previousCommunityData = queryClient.getQueryData(['community-discussions', user?.id]);
      const previousPaginatedData = queryClient.getQueriesData({ queryKey: ['community-discussions-paginated'] });
      const previousThreadData = queryClient.getQueryData(['discussion-thread-data', discussionId, user?.id]);
      
      // Optimistically remove from paginated queries (community page)
      queryClient.setQueriesData(
        { queryKey: ['community-discussions-paginated'] },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;
          
          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              discussions: page.discussions.filter((discussion: any) => 
                discussion.discussion_id !== discussionId
              ),
              pagination: {
                ...page.pagination,
                totalCount: Math.max(0, page.pagination.totalCount - 1)
              }
            }))
          };
        }
      );
      
      // Optimistically remove from legacy community discussions query
      queryClient.setQueryData(['community-discussions', user?.id], (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.filter((discussion: any) => discussion.id !== discussionId);
      });
      
      return { 
        previousCommunityData, 
        previousPaginatedData, 
        previousThreadData 
      };
    },
    onError: (err, discussionId, context) => {
      // Rollback optimistic updates on error
      if (context?.previousCommunityData) {
        queryClient.setQueryData(['community-discussions', user?.id], context.previousCommunityData);
      }
      
      if (context?.previousPaginatedData) {
        context.previousPaginatedData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      
      if (context?.previousThreadData) {
        queryClient.setQueryData(['discussion-thread-data', discussionId, user?.id], context.previousThreadData);
      }
      
      console.error('Error deleting discussion:', err);
    },
    onSuccess: (result, discussionId) => {
      // Invalidate relevant queries to ensure fresh data
      queryClient.invalidateQueries({ 
        queryKey: ['discussion-thread-data', discussionId, user?.id] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['community-discussions', user?.id] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['community-discussions-paginated'] 
      });
      
      // Return success result for navigation
      return result;
    },
  });
}; 